// src/pages/Customer/Orders/CustomerOrdersList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/layout/Header/Header";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import DirhamSymbol from "../../../Assets/aed-symbol.png";
import TableScrollSync from "../../../components/common/TableScrollSync";
import "./CustomerOrdersList.css";
import axios from "axios";
import toast from "../../../utils/toast";
import { useAppSettings } from "../../../context/AppSettingsContext";
import { usePaginatedData } from "../../../hooks/usePagination";
import Pagination from "../../../components/common/Pagination";
import OrderProductsModal from "../../../components/common/OrderProductsModal";

const CustomerOrdersList = () => {
  const navigate = useNavigate();
  const [realOrders, setRealOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Orders");
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewProductsOrder, setViewProductsOrder] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = "/login";
        return;
      }
      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data.user || response.data);
    } catch (error) {
      console.error("Failed to load user", error);
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [backendUrl]);

  const fetchCustomerHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${backendUrl}/api/orders/customer-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRealOrders(res.data.realOrders || []);

      // Only keep pending + rejected requests (approved ones are now real orders)
      const filteredRequests = (res.data.requests || []).filter(
        (req) => req.status === "pending" || req.status === "rejected"
      );
      setRequests(filteredRequests);
    } catch (err) {
      console.error("Error fetching customer history:", err);
      toast.error("Failed to load your order history");
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchCurrentUser();
    fetchCustomerHistory();
  }, [fetchCurrentUser, fetchCustomerHistory]);

  // ✅ Helper: Calculate VAT breakdown for order items
  const calculateOrderVAT = (orderItems, invoiceHistory) => {
    if (!orderItems || !Array.isArray(orderItems)) {
      return { exclVat: 0, vatAmount: 0, grandTotal: 0 };
    }

    let totalExclVat = 0;
    let totalVatAmount = 0;
    let totalGrand = 0;

    orderItems.forEach((item) => {
      const qty = item.orderedQuantity || 0;
      const price = item.price || 0;
      const vatPercent = item.vatPercentage || 5;

      const exclVat = qty * price;
      const vatAmount = (exclVat * vatPercent) / 100;
      const total = exclVat + vatAmount;

      totalExclVat += exclVat;
      totalVatAmount += vatAmount;
      totalGrand += total;
    });

    // Once packed, invoiceHistory holds the actual invoiced Grand Total(s),
    // already rounded to the nearest whole AED (Sub Total + Round Off =
    // Grand Total) — prefer that over the raw order-time estimate.
    const grandTotal = invoiceHistory?.length > 0
      ? invoiceHistory.reduce((sum, h) => sum + (h.amount || 0), 0)
      : totalGrand;

    return {
      exclVat: totalExclVat,
      vatAmount: totalVatAmount,
      grandTotal,
    };
  };

  const { entriesPerPage, invoiceSystemActive } = useAppSettings();

  // An order "has" an invoice once one has been generated for it — once
  // InvoiceCounter is deleted those orders are dropped entirely rather than
  // just hiding their invoice number. Requests never have invoices.
  const orderHasInvoice = (item) =>
    item.type === "order" &&
    (!!item.invoiceNumber ||
      !!item.deliveredInvoiceNumber ||
      (item.invoiceHistory && item.invoiceHistory.length > 0) ||
      (item.deliveredInvoiceHistory && item.deliveredInvoiceHistory.length > 0));

  // Combine real orders + pending/rejected requests into one list
  const filteredItems = useMemo(() => {
    const allItems = [
      ...realOrders.map((order) => ({ ...order, type: "order" })),
      ...requests.map((req) => ({ ...req, type: "request" })),
    ];

    return allItems.filter((item) => {
      const matchesSearch =
        !searchTerm.trim() ||
        item.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.orderItems?.some((item) =>
          item.product?.productName?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const itemStatus = item.type === "order" ? item.status : item.status;

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "pending_approval" &&
          item.type === "request" &&
          itemStatus === "pending") ||
        (statusFilter === "rejected" &&
          item.type === "request" &&
          itemStatus === "rejected") ||
        (item.type === "order" && itemStatus?.toLowerCase() === statusFilter.toLowerCase());

      const matchesInvoiceVisibility = invoiceSystemActive || !orderHasInvoice(item);

      return matchesSearch && matchesStatus && matchesInvoiceVisibility;
    });
  }, [realOrders, requests, searchTerm, statusFilter, invoiceSystemActive]);
  const pagination = usePaginatedData(
    filteredItems,
    entriesPerPage,
    `${statusFilter}|${searchTerm}`
  );

  const clearSearch = () => setSearchTerm("");

  const getAssignmentStatusDisplay = (item) => {
    if (item.type === "request") return "N/A"; // Requests don't have assignment
    if (!item.assignedTo) return "Not Assigned";
    if (item.assignmentStatus === "accepted") return "Accepted";
    if (item.assignmentStatus === "rejected") return "Rejected";
    if (item.assignmentStatus === "assigned") return "Assigned";
    return "Pending";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Returns true if the order is delivered and within 5 days
  const isReturnEligible = (item) => {
    if (item.type === "request") return false;
    if (item.status !== "delivered" && item.status !== "partial_delivered") return false;
    const deliveredDate = new Date(item.updatedAt);
    const diffDays = (Date.now() - deliveredDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 5;
  };

  // Returns true if an active (non-cancelled, non-rejected) return already exists
  const hasActiveReturn = (item) => {
    if (!item.salesReturn) return false;
    return !["cancelled", "rejected"].includes(item.salesReturn.status);
  };

  const getStatusDisplay = (item) => {
    if (item.type === "request") {
      if (item.status === "pending") return "Pending Approval";
      if (item.status === "rejected") return "Rejected";
    }
    return item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || "Pending";
  };

  const getStatusClass = (item) => {
    if (item.type === "request") {
      if (item.status === "pending") return "pending-approval";
      if (item.status === "rejected") return "rejected";
    }
    return item.status?.toLowerCase() || "pending";
  };

  if (!user) {
    return <div className="customer-orders-loading">Loading...</div>;
  }

  return (
    <div className="customer-orders-layout">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        user={user}
      />
      <Sidebar
        isOpen={sidebarOpen}
        activeItem={activeItem}
        onSetActiveItem={setActiveItem}
        onClose={() => setSidebarOpen(false)}
        user={user}
      />
      <main
        className={`customer-orders-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <div className="customer-orders-container-wrapper">
          <div className="customer-orders-container">
            <div className="customer-orders-header-section">
              <h2 className="customer-orders-page-title">My Orders</h2>

              <div className="customer-orders-controls-group">
                {/* Status Filter */}
                <div className="customer-orders-filter-group">
                  <label
                    htmlFor="statusFilter"
                    className="customer-orders-filter-label"
                  >
                    Filter by Status:
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="customer-orders-status-filter"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="rejected">Rejected</option>
                    <option value="partial_delivered">Partially Delivered</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Search */}
                <div className="customer-orders-search-container">
                  <input
                    type="text"
                    className="customer-orders-search-input"
                    placeholder="Search by customer or product..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search orders by customer or product name"
                  />
                  {searchTerm && (
                    <button
                      className="customer-orders-search-clear"
                      onClick={clearSearch}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>


              </div>
            </div>

            {loading ? (
              <div className="customer-orders-loading">Loading orders...</div>
            ) : filteredItems.length === 0 ? (
              <div className="customer-orders-no-data">
                No orders found
                {statusFilter !== "all" ? ` with status "${statusFilter}"` : ""}
                {searchTerm.trim() ? ` matching "${searchTerm}"` : ""}
              </div>
            ) : (
              <>
                <TableScrollSync>
                  <div className="customer-orders-table-wrapper">
                    <table className="customer-orders-data-table">
                      <thead>
                        <tr>
                          <th scope="col">No</th>
                          <th scope="col">Order ID</th>
                          <th scope="col">Total Ordered Qty</th>
                          <th scope="col">Total Delivered Qty</th>
                          {/* ✅ UPDATED: VAT Breakdown Columns */}
                          <th scope="col" className="vat-col">Total Dhs<br/><small>(Excl. VAT)</small></th>
                          <th scope="col" className="vat-col">VAT 5%<br/><small>(Amount)</small></th>
                          <th scope="col" className="vat-col grand-total-col">Grand Total<br/><small>(Incl. VAT)</small></th>
                          <th scope="col">Remarks</th>
                          <th scope="col">Payment</th>
                          <th scope="col">Delivery Partner</th>
                          <th scope="col">Status</th>
                          <th scope="col">Order Date</th>
                          <th scope="col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.pageData.map((item, index) => {
                          const isRequest = item.type === "request";

                          // For real orders
                          const totalOrdered = isRequest
                            ? item.orderItems?.reduce((sum, it) => sum + it.orderedQuantity, 0) || 0
                            : item.orderItems?.reduce((sum, it) => sum + it.orderedQuantity, 0) || 0;

                          const totalDelivered = isRequest
                            ? 0 // Requests have no delivered qty
                            : item.orderItems?.reduce((sum, it) => sum + it.deliveredQuantity, 0) || 0;

                          // ✅ Calculate VAT breakdown
                          const { exclVat, vatAmount, grandTotal } = calculateOrderVAT(item.orderItems, item.invoiceHistory);

                          return (
                            <tr key={item._id}>
                              <td>{pagination.showingFrom + index}</td>
                              <td>
                                {item.orderItems?.length > 0 ? (
                                  <button
                                    type="button"
                                    className="customer-orders-orderid-link"
                                    onClick={() => setViewProductsOrder(item)}
                                    title="View ordered products"
                                  >
                                    {item.type === "order"
                                      ? item.orderId || item._id
                                      : `REQ-${item._id.toString().slice(-6)}`}
                                  </button>
                                ) : item.type === "order" ? (
                                  item.orderId || item._id
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>{totalOrdered}</td>
                              <td>{totalDelivered}</td>

                              {/* ✅ UPDATED: VAT Breakdown Display */}
                              <td className="vat-cell">
                                <div className="vat-amount">
                                  <img src={DirhamSymbol} alt="AED" width={12} />
                                  <span>{exclVat.toFixed(2)}</span>
                                </div>
                              </td>
                              <td className="vat-cell">
                                <div className="vat-amount vat-highlight">
                                  <img src={DirhamSymbol} alt="AED" width={12} />
                                  <span>{vatAmount.toFixed(2)}</span>
                                </div>
                              </td>
                              <td className="vat-cell grand-total-cell">
                                <div className="vat-amount grand-total-amount">
                                  <img src={DirhamSymbol} alt="AED" width={14} />
                                  <span>{grandTotal.toFixed(2)}</span>
                                </div>
                              </td>

                              <td>{item.remarks || "-"}</td>

                              <td>
                                {item.payment?.charAt(0).toUpperCase() +
                                  item.payment?.slice(1) || "N/A"}
                              </td>

                              <td>
                                <span
                                  className={`customer-orders-assignment-badge customer-orders-assignment-${
                                    isRequest ? "pending" : (item.assignmentStatus?.toLowerCase() || "pending")
                                  }`}
                                >
                                  {isRequest ? "N/A" : getAssignmentStatusDisplay(item)}
                                </span>
                              </td>

                              <td>
                                <span
                                  className={`customer-orders-status-badge customer-orders-status-${getStatusClass(item)}`}
                                >
                                  {getStatusDisplay(item)}
                                </span>
                              </td>

                              <td>
                                {isRequest
                                  ? formatDate(item.requestedAt)
                                  : formatDate(item.orderDate)}
                              </td>

                              <td>
                                {item.salesReturn?.status === "completed" ? (
                                  <span className="co-return-na">Returned</span>
                                ) : isReturnEligible(item) ? (
                                  hasActiveReturn(item) ? (
                                    <button
                                      className="co-btn-return"
                                      disabled
                                      title={`Return already submitted (${item.salesReturn.status})`}
                                      style={{ opacity: 0.5, cursor: "not-allowed" }}
                                    >
                                      ↩ Returned
                                    </button>
                                  ) : (
                                    <button
                                      className="co-btn-return"
                                      onClick={() =>
                                        navigate(`/sales-returns/create?orderId=${item._id}`)
                                      }
                                    >
                                      ↩ Return
                                    </button>
                                  )
                                ) : (
                                  <span className="co-return-na">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </TableScrollSync>

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalRecords={pagination.totalRecords}
                  showingFrom={pagination.showingFrom}
                  showingTo={pagination.showingTo}
                  canPrev={pagination.canPrev}
                  canNext={pagination.canNext}
                  onPrev={pagination.goPrev}
                  onNext={pagination.goNext}
                />
              </>
            )}
          </div>
        </div>
      </main>

      {viewProductsOrder && (
        <OrderProductsModal
          order={viewProductsOrder}
          onClose={() => setViewProductsOrder(null)}
        />
      )}
    </div>
  );
};

export default CustomerOrdersList;